
import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WorkflowCard from "@/components/WorkflowCard";
import SearchFilter from "@/components/SearchFilter";
import { useSearchParams } from "react-router-dom";

// Mock workflow data
const mockWorkflows = [
  {
    id: "wf1",
    title: "Customer Onboarding Automation",
    description: "Automate your customer onboarding process with this comprehensive Zapier workflow.",
    price: 29.99,
    rating: 4.8,
    reviewCount: 124,
    platform: "Zapier",
    category: "Sales",
    image: "https://images.unsplash.com/photo-1518893494013-481c1d8ed3fd?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
  },
  {
    id: "wf2",
    title: "Social Media Content Calendar",
    description: "Schedule and organize your social media content across multiple platforms.",
    price: 19.99,
    rating: 4.5,
    reviewCount: 97,
    platform: "Make.com",
    category: "Marketing",
    image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
  },
  {
    id: "wf3",
    title: "Employee Onboarding Process",
    description: "Streamline your HR processes with this employee onboarding workflow.",
    price: 34.99,
    rating: 4.9,
    reviewCount: 56,
    platform: "n8n",
    category: "HR",
    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
  },
  {
    id: "wf4",
    title: "Invoice Processing Automation",
    description: "Automatically process and track invoices with this accounting workflow.",
    price: 39.99,
    rating: 4.7,
    reviewCount: 78,
    platform: "Zapier",
    category: "Finance",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
  },
  {
    id: "wf5",
    title: "Lead Qualification System",
    description: "Score and qualify leads automatically based on custom criteria.",
    price: 24.99,
    rating: 4.3,
    reviewCount: 112,
    platform: "Make.com",
    category: "Sales",
    image: "https://images.unsplash.com/photo-1550305080-4e029753abcf?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
  },
  {
    id: "wf6",
    title: "Project Management Dashboard",
    description: "Track project progress and team performance with this automated dashboard.",
    price: 49.99,
    rating: 4.6,
    reviewCount: 45,
    platform: "Notion",
    category: "Operations",
    image: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
  },
];

const Marketplace = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredWorkflows, setFilteredWorkflows] = useState(mockWorkflows);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState({
    platform: searchParams.get("category") || "all",
    category: "all",
    priceRange: [0, 100],
    rating: "all"
  });

  useEffect(() => {
    // Update page title
    document.title = "Marketplace - FlowMarket";
    
    // Apply initial category filter from URL if present
    if (searchParams.get("category")) {
      handleFilterChange({
        ...activeFilters,
        platform: searchParams.get("category") || "all"
      });
    }
  }, []);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    applyFilters(term, activeFilters);
  };

  const handleFilterChange = (filters: any) => {
    setActiveFilters(filters);
    applyFilters(searchTerm, filters);
    
    // Update URL with platform filter
    if (filters.platform && filters.platform !== "all") {
      setSearchParams({ category: filters.platform });
    } else {
      setSearchParams({});
    }
  };

  const applyFilters = (term: string, filters: any) => {
    let results = mockWorkflows;
    
    // Apply search term filter
    if (term) {
      results = results.filter(
        (workflow) =>
          workflow.title.toLowerCase().includes(term.toLowerCase()) ||
          workflow.description.toLowerCase().includes(term.toLowerCase())
      );
    }
    
    // Apply platform filter
    if (filters.platform && filters.platform !== "all") {
      results = results.filter(
        (workflow) => workflow.platform.toLowerCase() === filters.platform.toLowerCase()
      );
    }
    
    // Apply category filter
    if (filters.category && filters.category !== "all") {
      results = results.filter(
        (workflow) => workflow.category.toLowerCase() === filters.category.toLowerCase()
      );
    }
    
    // Apply rating filter
    if (filters.rating && filters.rating !== "all") {
      results = results.filter(
        (workflow) => workflow.rating >= parseInt(filters.rating)
      );
    }
    
    // Apply price range filter
    results = results.filter(
      (workflow) =>
        workflow.price >= filters.priceRange[0] && workflow.price <= filters.priceRange[1]
    );
    
    setFilteredWorkflows(results);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow container mx-auto px-4 md:px-6 py-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">Workflow Marketplace</h1>
        <p className="text-muted-foreground mb-8">
          Browse our collection of pre-built automation workflows for your favorite no-code tools.
        </p>
        
        <SearchFilter onSearch={handleSearch} onFilterChange={handleFilterChange} />
        
        {filteredWorkflows.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWorkflows.map((workflow) => (
              <WorkflowCard key={workflow.id} {...workflow} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <h3 className="text-2xl font-medium mb-2">No workflows found</h3>
            <p className="text-muted-foreground mb-6">
              Try adjusting your search or filters to find what you're looking for.
            </p>
            <button 
              onClick={() => {
                handleFilterChange({
                  platform: "all",
                  category: "all",
                  priceRange: [0, 100],
                  rating: "all"
                });
                setSearchTerm("");
              }}
              className="text-primary hover:underline"
            >
              Reset all filters
            </button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Marketplace;
